import type { AvatarProps } from "@octopusdeploy/design-system-components";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import { type ReactElement } from "react";
import type { RnDTeam } from "~/RnDTeam";
import { UpdateNotificationsWorkflow, updateNotificationsWorkflowLoader } from "~/areas/configuration/components/License/UpdateNotificationsWorkflow";
import { AddJiraServiceManagementConnectionWorkflow } from "~/areas/configuration/components/Settings/AddJiraServiceManagementConnectionWorkflow";
import { EditJiraServiceManagementConnectionWorkflow } from "~/areas/configuration/components/Settings/EditJiraServiceManagementConnectionWorkflow";
import { EditSigningKeyConfigurationWorkflow, editSigningKeyConfigurationWorkflowLoader } from "~/areas/configuration/components/SigningKeys/EditSigningKeyConfigurationWorkflow";
import { RotateExternalSigningKeyWorkflow, rotateExternalSigningKeyWorkflowLoader } from "~/areas/configuration/components/SigningKeys/RotateExternalSigningKeyWorkflow";
import { AddSpaceWorkflow, addSpaceWorkflowLoader } from "~/areas/configuration/components/Spaces/AddSpaceWorkflow";
import { EditParentEnvironmentWorkflow } from "~/areas/infrastructure/components/ParentEnvironmentLayout/EditParentEnvironmentWorkflow";
import { UploadPackageWorkflow } from "~/areas/library/components/BuiltInRepository/UploadPackageWorkflow";
import { AddCertificateWorkflow, addCertificateWorkflowLoader, addCertificateWorkflowTitle } from "~/areas/library/components/Certificates/Certificate/AddCertificateWorkflow";
import { ViewProjectTemplateUsagesWorkflow } from "~/areas/platformHub/projectTemplates/ProjectTemplatesPage";
import { EditChannelWorkflow } from "~/areas/projects/components/Channels/EditChannelWorkflow";
import { AddFeatureToggleEnvironmentWorkflow, EditFeatureToggleEnvironmentWorkflow, featureToggleEnvironmentWorkflowLoader } from "~/areas/projects/components/FeatureToggles/Workflows/FeatureToggleEnvironmentWorkflow";
import type { MappedWorkflowRegistrations } from "~/workflows/MappedWorkflowRegistrations";
import type { DialogWorkflow, DrawerWorkflow, NoWorkflowParameter } from "~/workflows/workflows";
import { workflows } from "~/workflows/workflows";
export const workflowRegistrations = {
    addSpaceWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.addSpaceWorkflow,
        loader: (repository) => addSpaceWorkflowLoader(repository),
        render: ({ completeWorkflow, loaderData }) => <AddSpaceWorkflow loaderData={loaderData} onSpaceCreated={completeWorkflow}/>,
        owner: "FrontendFoundations",
    }),
    addCertificateWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.addCertificateWorkflow,
        loader: (repository, { input }) => addCertificateWorkflowLoader(repository, input),
        loadingTitle: addCertificateWorkflowTitle,
        loadingActions: {
            primaryActionLabel: "Save",
        },
        render: ({ input, completeWorkflow, loaderData }) => <AddCertificateWorkflow input={input} completeWorkflow={completeWorkflow} loaderData={loaderData}/>,
        owner: "ProductGrowth",
    }),
    viewProjectTemplateUsagesWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.viewProjectTemplateUsagesWorkflow,
        render: ({ input, completeWorkflow }) => <ViewProjectTemplateUsagesWorkflow input={input} completeWorkflow={completeWorkflow}/>,
        owner: "ModellingAtScale",
        hideCancelButton: true,
        width: "1000px",
    }),
    addJiraServiceManagementConnectionWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.addJiraServiceManagementConnectionWorkflow,
        render: ({ input, completeWorkflow }) => <AddJiraServiceManagementConnectionWorkflow workflowInput={input} completeWorkflow={completeWorkflow}/>,
        owner: "ProductGrowth",
    }),
    editJiraServiceManagementConnectionWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.editJiraServiceManagementConnectionWorkflow,
        render: ({ input, completeWorkflow }) => <EditJiraServiceManagementConnectionWorkflow workflowInput={input} completeWorkflow={completeWorkflow}/>,
        owner: "ProductGrowth",
    }),
    editChannelWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.editChannelWorkflow,
        render: ({ input, completeWorkflow }) => <EditChannelWorkflow workflowInput={input} completeWorkflow={completeWorkflow}/>,
        owner: "DeveloperExperience",
    }),
    editParentEnvironmentWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.editParentEnvironmentWorkflow,
        render: ({ input, completeWorkflow }) => <EditParentEnvironmentWorkflow workflowInput={input} completeWorkflow={completeWorkflow}/>,
        owner: "DeveloperExperience",
    }),
    uploadPackageWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.uploadPackageWorkflow,
        render: ({ completeWorkflow }) => <UploadPackageWorkflow completeWorkflow={completeWorkflow}/>,
        owner: "ModellingAtScale",
    }),
    addFeatureToggleEnvironmentWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.addFeatureToggleEnvironmentWorkflow,
        loader: (repository, { input }) => featureToggleEnvironmentWorkflowLoader(repository, input.spaceId, input.projectId),
        render: ({ input, completeWorkflow, loaderData }) => <AddFeatureToggleEnvironmentWorkflow workflowInput={input} completeWorkflow={completeWorkflow} loaderData={loaderData}/>,
        owner: "DeveloperExperience",
    }),
    editFeatureToggleEnvironmentWorkflow: createDrawerWorkflowRegistration({
        workflow: workflows.editFeatureToggleEnvironmentWorkflow,
        loader: (repository, { input }) => featureToggleEnvironmentWorkflowLoader(repository, input.spaceId, input.projectId),
        render: ({ input, completeWorkflow, loaderData }) => <EditFeatureToggleEnvironmentWorkflow workflowInput={input} completeWorkflow={completeWorkflow} loaderData={loaderData}/>,
        owner: "DeveloperExperience",
    }),
    editSigningKeyConfigurationWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.editSigningKeyConfigurationWorkflow,
        loader: (repository) => editSigningKeyConfigurationWorkflowLoader(repository),
        render: ({ completeWorkflow, loaderData }) => <EditSigningKeyConfigurationWorkflow loaderData={loaderData} completeWorkflow={completeWorkflow}/>,
        owner: "DeveloperExperience",
    }),
    updateNotificationsWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.updateNotificationsWorkflow,
        loader: (repository) => updateNotificationsWorkflowLoader(repository),
        render: ({ completeWorkflow, loaderData }) => <UpdateNotificationsWorkflow loaderData={loaderData} completeWorkflow={completeWorkflow}/>,
        owner: "FrontendFoundations",
    }),
    rotateExternalSigningKeyWorkflow: createDialogWorkflowRegistration({
        workflow: workflows.rotateExternalSigningKeyWorkflow,
        loader: (repository) => rotateExternalSigningKeyWorkflowLoader(repository),
        render: ({ input, completeWorkflow, loaderData }) => <RotateExternalSigningKeyWorkflow input={input} loaderData={loaderData} completeWorkflow={completeWorkflow}/>,
        owner: "DeveloperExperience",
    }),
} satisfies MappedWorkflowRegistrations<typeof workflows>;
export type WorkflowRegistration<Input, Output, LoaderData> = DialogWorkflowRegistration<Input, Output, LoaderData> | DrawerWorkflowRegistration<Input, Output, LoaderData>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UnknownWorkflowRegistration = WorkflowRegistration<any, any, any>;
export const allWorkflowRegistrations: UnknownWorkflowRegistration[] = Object.values(workflowRegistrations);
export interface DialogWorkflowRegistration<Input, Output, LoaderData> {
    workflow: DialogWorkflow<Input, Output>;
    loader: WorkflowLoader<Input, LoaderData> | undefined;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData | null>) => ReactElement;
    owner: RnDTeam;
    hideCancelButton: boolean;
    width: DialogWidth | undefined;
}
export interface DrawerWorkflowRegistration<Input, Output, LoaderData> {
    workflow: DrawerWorkflow<Input, Output>;
    loader: WorkflowLoader<Input, LoaderData> | undefined;
    loadingTitle: string | undefined;
    subtitle: string | undefined;
    avatar: RenderAvatar<Input> | undefined;
    width: DrawerWidth | undefined;
    loadingActions: LoadingActions | undefined;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData>) => ReactElement;
    owner: RnDTeam;
    hideCancelButton: boolean;
}
export interface LoadingActions {
    primaryActionLabel?: string;
    actions?: LoadingAction[];
}
export interface LoadingAction {
    label: string;
    buttonType: "secondary" | "tertiary" | "destructive";
}
export function isDialogWorkflowRegistration<Input, Output, LoaderData>(workflowRegistration: WorkflowRegistration<Input, Output, LoaderData>): workflowRegistration is DialogWorkflowRegistration<Input, Output, LoaderData> {
    return workflowRegistration.workflow.workflowType === "dialog";
}
interface DrawerWorkflowConfiguration<Input, Output, LoaderData> {
    workflow: DrawerWorkflow<Input, Output>;
    loader?: WorkflowLoader<Input, LoaderData>;
    loadingTitle?: string;
    subtitle?: string;
    avatar?: RenderAvatar<Input>;
    width?: DrawerWidth;
    loadingActions?: LoadingActions;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData>) => ReactElement;
    owner: RnDTeam;
    hideCancelButton?: boolean;
}
type DrawerWidth = "wide" | "medium" | "narrow";
type RenderAvatar<Input> = (params: OmitWorkflowInputIfNotRequired<{
    input: Input;
}>) => ReactElement<AvatarProps>;
interface DialogWorkflowConfiguration<Input, Output, LoaderData> {
    workflow: DialogWorkflow<Input, Output>;
    loader?: WorkflowLoader<Input, LoaderData>;
    render: (props: WorkflowRenderProps<Input, Output, LoaderData | null>) => ReactElement;
    owner: RnDTeam;
    hideCancelButton?: boolean;
    width?: DialogWidth;
}
type DialogWidth = "480px" | "600px" | "800px" | "1000px";
export function createDialogWorkflowRegistration<Input, Output, LoaderData>({ workflow, loader, render, owner, width, hideCancelButton }: DialogWorkflowConfiguration<Input, Output, LoaderData>): DialogWorkflowRegistration<Input, Output, LoaderData> {
    return {
        workflow,
        loader,
        render,
        owner,
        hideCancelButton: hideCancelButton ?? false,
        width,
    };
}
export function createDrawerWorkflowRegistration<Input, Output, LoaderData>({ workflow, loader, loadingTitle, subtitle, avatar, width, render, loadingActions, owner, hideCancelButton, }: DrawerWorkflowConfiguration<Input, Output, LoaderData>): DrawerWorkflowRegistration<Input, Output, LoaderData> {
    return {
        workflow,
        loader,
        loadingTitle,
        subtitle,
        avatar,
        width,
        render,
        loadingActions,
        owner,
        hideCancelButton: hideCancelButton ?? false,
    };
}
type WorkflowRenderProps<Input, Output, LoaderData> = OmitWorkflowInputIfNotRequired<{
    input: Input;
    loaderData: LoaderData;
    completeWorkflow: CompleteWorkflow<Output>;
}>;
export type CompleteWorkflow<Output> = Output extends NoWorkflowParameter ? () => void : (output: Output) => void;
type WorkflowLoader<Input, LoaderData> = (repository: Repository, parameters: OmitWorkflowInputIfNotRequired<{
    input: Input;
}>) => Promise<LoaderData>;
export type OmitWorkflowInputIfNotRequired<T extends {
    input: unknown;
}> = T extends {
    input: infer Input;
} ? (Input extends NoWorkflowParameter ? Omit<T, "input"> : T) : never;
